import RESTClient from 'web-app/api/clients/rest-client-famly-app';

import * as Actions from '../actions';
import type * as Types from '../types';

let manager: any = null;

// -- Observers --
const audioVideoObserver = {
    videoTileDidUpdate: (tileState: Types.TileState) => {
        // This observer is always called twice when an attendee enables
        // the camera for some reason 🤷‍♂️
        Actions.tileDidUpdate.dispatch(tileState);
    },
    videoTileWasRemoved: (tileId: number) => {
        // This is called when an attendee disables their video feed
        Actions.tileDeleted.dispatch(tileId);
    },
};

const deviceChangeObserver = {
    audioInputsChanged: (freshAudioInputDeviceList: MediaDeviceInfo[]) => {
        Actions.updateAudioInputDeviceList.dispatch(freshAudioInputDeviceList);
    },
    audioOutputsChanged: (freshAudioOutputDeviceList: MediaDeviceInfo[]) => {
        Actions.updateAudioOutputDeviceList.dispatch(freshAudioOutputDeviceList);
    },
    videoInputsChanged: (freshVideoInputDeviceList: MediaDeviceInfo[]) => {
        Actions.updateVideoInputDeviceList.dispatch(freshVideoInputDeviceList);
    },
};

const makeManager = () => {
    return import('amazon-chime-sdk-js').then(AmazonChimeSdk => {
        const {
            ConsoleLogger,
            DefaultDeviceController,
            DefaultMeetingSession,
            LogLevel,
            MeetingSessionConfiguration,
            DefaultMeetingReadinessChecker,
            CheckAudioInputFeedback,
        } = AmazonChimeSdk;

        // Get the types from the prototype
        type DefaultMeetingSessionType = typeof DefaultMeetingSession.prototype;
        type MeetingSessionConfigurationType = typeof MeetingSessionConfiguration.prototype;
        type DefaultMeetingReadinessCheckerType = typeof DefaultMeetingReadinessChecker.prototype;
        // These are interfaces so they are not exported from the build 'amazon-chime-sdk-js'.
        type AudioVideoFacadeType = DefaultMeetingSessionType['audioVideo'];

        class MeetingManager {
            private meetingSession?: DefaultMeetingSessionType;
            private audioVideo?: AudioVideoFacadeType;
            private meetingReadinessChecker?: DefaultMeetingReadinessCheckerType;

            // --- Meeting Controls ---
            async joinMeeting(meetingId: string, joinToken?: string): Promise<void> {
                // If a 'joinToken' is provided, pass it to an unauthorized API call
                const data = joinToken
                    ? await RESTClient.post(
                          `v2/famly-live/meetings/${meetingId}/join`,
                          {
                              joinToken,
                          },
                          {suppressErrors: true},
                      )
                    : await RESTClient.postAuthenticated(
                          `v2/famly-live/meetings/${meetingId}/join`,
                          {},
                          {suppressErrors: true},
                      );
                // The refresh token is only returned if we provided a 'joinToken'
                const refreshToken = data.refreshToken;
                if (refreshToken) {
                    Actions.setRefreshToken.dispatch(refreshToken);
                }
                await this.initializeMeetingSession(
                    new MeetingSessionConfiguration(data.meeting.awsMeeting, data.me.awsAttendee),
                );
                if (this.audioVideo) {
                    this.audioVideo.start();
                }
            }

            leaveMeeting(): void {
                if (this.meetingSession && this.audioVideo) {
                    this.teardownAudioVideoObserver();
                    this.teardownAvailableDeviceChangedObservers();
                    this.audioVideo.stop();
                    this.audioVideo.stopAudioInput();
                    this.audioVideo.stopVideoInput();
                }
            }

            async chooseAudioInputDevice(deviceId: string | null): Promise<void> {
                if (this.audioVideo) {
                    await this.audioVideo.startAudioInput(deviceId);
                }
            }

            async chooseAudioOutputDevice(deviceId: string | null): Promise<void> {
                if (this.audioVideo) {
                    await this.audioVideo.chooseAudioOutput(deviceId);
                }
            }

            async chooseVideoInputDevice(deviceId: string | null): Promise<void> {
                if (this.audioVideo) {
                    if (deviceId) {
                        await this.audioVideo.startVideoInput(deviceId);
                    }
                }
            }

            async startLocalVideo(deviceId: string | null): Promise<void> {
                await this.chooseVideoInputDevice(deviceId);
                if (this.audioVideo) {
                    this.audioVideo.startLocalVideoTile();
                }
            }

            stopLocalVideo(): void {
                if (this.audioVideo) {
                    this.audioVideo.stopLocalVideoTile();
                }
            }

            async muteLocalAudio(): Promise<void> {
                if (this.audioVideo) {
                    this.audioVideo.realtimeMuteLocalAudio();
                }
            }

            async unmuteLocalAudio(): Promise<void> {
                if (this.audioVideo) {
                    this.audioVideo.realtimeUnmuteLocalAudio();
                }
            }

            // --- Bind / Unbind elements ---
            bindAudioElement(ref: HTMLAudioElement): void {
                if (this.audioVideo) {
                    this.audioVideo.bindAudioElement(ref);
                }
            }

            unbindAudioElement(): void {
                if (this.audioVideo) {
                    this.audioVideo.unbindAudioElement();
                }
            }

            bindVideoTile(id: number, videoEl: HTMLVideoElement): void {
                if (this.audioVideo) {
                    this.audioVideo.bindVideoElement(id, videoEl);
                }
            }

            // -- Setup --
            private async initializeMeetingSession(configuration: MeetingSessionConfigurationType): Promise<void> {
                const logger = new ConsoleLogger('DEV-SDK', LogLevel.WARN);
                const logger2 = new ConsoleLogger('READY-CHECK', LogLevel.WARN);
                const deviceController = new DefaultDeviceController(logger, {enableWebAudio: true});
                const meetingSession = new DefaultMeetingSession(configuration, logger, deviceController);
                this.meetingReadinessChecker = new DefaultMeetingReadinessChecker(logger2, meetingSession);
                this.meetingSession = meetingSession;
                this.audioVideo = meetingSession.audioVideo;
                this.setupAttendeeSubscriber();
                this.setupAvailableDevicesChangedObservers();
                await this.setupAudioDevices();
                await this.setupVideoDevices();
            }

            private async setupAudioDevices(): Promise<void> {
                if (this.audioVideo) {
                    const audioInput = await this.audioVideo.listAudioInputDevices();
                    const audioOutput = await this.audioVideo.listAudioOutputDevices();

                    Actions.updateAudioInputDeviceList.dispatch(audioInput);
                    Actions.updateAudioOutputDeviceList.dispatch(audioOutput);

                    // If the audio is disabled it either returns null or
                    // an object where the deviceId === ''
                    const defaultInput = audioInput[0]?.deviceId;
                    const defaultOutput = audioOutput[0]?.deviceId;

                    const audioInputFeedback = await this.meetingReadinessChecker?.checkAudioInput(audioInput[0]);
                    const audioInputOK = audioInputFeedback === CheckAudioInputFeedback.Succeeded;

                    if (!defaultInput || !audioInputOK) {
                        Actions.deviceNotFoundError.dispatch('AUDIO_INPUT');
                    }
                    if (!defaultOutput) {
                        Actions.deviceNotFoundError.dispatch('AUDIO_OUTPUT');
                    }
                }
            }

            private async setupVideoDevices(): Promise<void> {
                if (this.audioVideo) {
                    const videoInputDevices = await this.audioVideo.listVideoInputDevices();
                    const defaultVideo = videoInputDevices[0];
                    // If the video is disabled it either returns null or
                    // an object where the deviceId === ''
                    if (!defaultVideo || defaultVideo.deviceId === '') {
                        Actions.deviceNotFoundError.dispatch('VIDEO_INPUT');
                        Actions.updateVideoInputDeviceList.dispatch([]);
                    } else {
                        Actions.updateVideoInputDeviceList.dispatch(videoInputDevices);
                    }
                    this.setupAudioVideoObserver();
                }
            }

            // --- Setup / teardown observers ---
            private setupAvailableDevicesChangedObservers(): void {
                if (this.audioVideo) {
                    this.audioVideo.addDeviceChangeObserver(deviceChangeObserver);
                }
            }

            private teardownAvailableDeviceChangedObservers(): void {
                if (this.audioVideo) {
                    this.audioVideo.removeDeviceChangeObserver(deviceChangeObserver);
                }
            }

            private setupAudioVideoObserver(): void {
                if (this.audioVideo) {
                    this.audioVideo.addObserver(audioVideoObserver);
                }
            }

            private teardownAudioVideoObserver(): void {
                if (this.audioVideo) {
                    this.audioVideo.removeObserver(audioVideoObserver);
                }
            }

            // --- Setup Subscribers ---
            private subscribeToVolumeIndicator(presentAttendeeId: string) {
                const callback = (
                    attendeeId: string,
                    volume: number | null,
                    muted: boolean | null,
                    signalStrength: number | null,
                ) => {
                    Actions.attendeeVolumeInfoDidChange.dispatch(attendeeId, volume, muted, signalStrength);
                };
                if (this.audioVideo) {
                    this.audioVideo.realtimeSubscribeToVolumeIndicator(presentAttendeeId, callback);
                }
            }

            private setupAttendeeSubscriber() {
                const callback = (presentAttendeeId: string, present: boolean) => {
                    if (!present) {
                        Actions.removeAttendeeId.dispatch(presentAttendeeId);
                        Actions.removeAttendeeVolumeInfo.dispatch(presentAttendeeId);
                    } else {
                        Actions.addAttendeeId.dispatch(presentAttendeeId);
                        this.subscribeToVolumeIndicator(presentAttendeeId);
                    }
                };
                if (this.audioVideo) {
                    this.audioVideo.realtimeSubscribeToAttendeeIdPresence(callback);
                }
            }
        }

        if (!manager) {
            manager = new MeetingManager();
        }

        return manager as MeetingManager;
    });
};

export default makeManager;
